import { Toast } from "primereact/toast";
import React, { useEffect, useReducer, useRef, useState } from "react";
import {
  FormArray,
  FormControl,
  FormGroup,
  useFormConfig,
  Validators,
  WForm,
  WFormArrayElement,
  WFormControl,
  WFormGroup,
} from "rectangular-forms";
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  from,
  map,
  mergeMap,
  of,
  tap,
} from "rxjs";

import { Authorization } from "../../common/AuthorizationCpt";
import {
  onlyDigitsNumbersLetters,
  WCalendar,
  WInputCurrency,
  WInputText,
} from "../../common/components/TemplateContainer/InputTemplates";
import { TemplateInputContainer } from "../../common/components/TemplateContainer/TemplateContainer";
import icArrowCircleDownFill from "../../images/ic_arrow-circle-down-fill.svg";
import icArrowCircleUpFill from "../../images/ic_arrow-circle-up-fill.svg";
import icChecklistRounded from "../../images/ic_checklist_rounded.svg";
import icCircleCheckFill from "../../images/ic_circle-check-fill.svg";
import icIconDetail from "../../images/ic_detail_icon.svg";
import icDownload from "../../images/ic_download.svg";
import icInfoBlack from "../../images/ic_info_black.svg";
import icInfoRed from "../../images/ic_info_red.svg";
import icPDF from "../../images/ic_pdf.svg";
import icSaveFloppyDisk from "../../images/ic_save_floppy_disk.svg";
import checkIcon from "../../images/icon_check.svg";
import redCrossIcon from "../../images/icon_cross_red.svg";
import { HTTP_METHODS } from "../../utils/constants";
import { networkBackendRequest } from "../../utils/networkUtils";
import { formatMoney, formatMoneyNumber } from "../../utils/numbers";
import AdminAskForAction from "../AdminAskForAction/AdminAskForAction";
import {
  ApproveButton,
  RejectButton,
} from "../AdminDocumentManagement/AdminDocumentManagementSharedStyles";
import Sidebar from "../Sidebar/Sidebar";
import {
  AmountDifference,
  ButtonGeneric,
  ButtonsContainer,
  Container,
  ContainerInfo,
  DetailButton,
  DivFlexCenterIcon,
  DivFlexCenterQuotation,
  DivJustifyScreenplay,
  DivQuotationSide,
  ErrorMessage,
  HorizontalContainer,
  HorizontalLine,
  LabelQuotationTci,
  MessageInformative,
  PWarningComment,
  RowPers,
  SectionTitle,
  Subtitle,
  TableBodyPers,
  TableHeaderPers,
  Text,
  Title,
} from "./AdminPaymentValidationStatesSidebarStyles";
import { CompensationCode } from "./modals/compensationCode";
import { ConpensationDetail } from "./modals/compensationDetail";
import { ConfirmationModal } from "./modals/modalConfimation";
import { ModalOperationSuccess } from "./modals/modalOperationSucces";
import { GroupError } from "./validation/GroupErrorValidation";
import { format, parse } from "date-fns";
import { InvoiceConvert } from "../AdminPaymentValidation/AdminPaymentValidation.interface";

enum substatesEnum {
  to_compensate = "to_compensate",
  pending = "pending",
  conpensated = "conpensated",
  compensating = "compensating",
}

enum statusAmount {
  faltante = "faltante",
  saldo_a_favor = "Saldo a Favor",
  exacto = "Exacto",
}

const operationNumberValidation = (forceUpdate) => (group) => {
  const bank = group.get("bank").value;
  const docNumber = group.get("numeroOperacion").value;
  const subject: any = new BehaviorSubject("");
  subject.next({ bank: bank, docNumber: docNumber });
  const debouncerTime$ = subject.asObservable().pipe(
    debounceTime(1000),
    mergeMap((data: any) => {
      const params = {
        bank_name: data.bank,
        operation_number: data.docNumber,
      };
      return from(
        networkBackendRequest(
          "/vouchers/is-valid/",
          HTTP_METHODS.GET,
          params,
          undefined
        ).then((resp) => resp.json())
      ).pipe(
        map((resp: any) => {
          return !resp.is_valid
            ? {
                isNumberFree: resp.is_valid,
                numberOperation: resp.operation_number,
                numberInvoice: resp.invoice_numeric_id,
              }
            : null;
        }),
        tap(
          () => {
            setTimeout(() => {
              forceUpdate();
            }, 10);
          },
          () => {}
        ),
        catchError(() => {
          return of(null);
        })
      );
    })
  );
  return debouncerTime$;
};

const montoDepositoValidator = (control) => {
  const subject = new BehaviorSubject("");
  subject.next(control.value);
  const debouncerTime$ = subject.asObservable().pipe(
    map((data) => {
      const dataNumber = +data;

      const isBig = dataNumber > 100000000;
      return isBig ? { exedQuantity: isBig } : null;
    })
  );
  return debouncerTime$;
};

interface Props {
  onOutsideClick: () => void;
  handleRejectionComment: (elem: InvoiceConvert, newStatus: string) => void;
  sidebarInfo: InvoiceConvert;
  setShowSidebarModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleAskForAction: (
    elem: InvoiceConvert,
    vouchers: string[],
    newStatus: string
  ) => void;
  onErrorEndpoint: (error: any) => void;
  respData: any;
  onOk: () => void;
}

export const AdminPaymentValidationStatesSidebar = (props: Props) => {
  const {
    onOutsideClick,
    handleRejectionComment,
    sidebarInfo,
    setShowSidebarModal,
    handleAskForAction,
    onErrorEndpoint,
    respData,
  } = props;

  const [pollPayment, setPollPayment] = useState<InvoiceConvert>({});
  useEffect(() => {
    const found = respData.data.find(
      (invoice: InvoiceConvert) => invoice.invoiceId === sidebarInfo.invoiceId
    );
    setPollPayment(found);
  }, [respData, sidebarInfo]);

  const toast = useRef(null);

  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const handleDownload = (document: string) => {
    generateDownloadUrl(document).then((url) => {
      window.open(url, "_blank");
    });
  };

  const status = sidebarInfo.paymentStatus.option;
  const substatus =
    sidebarInfo.cashbackStatus.option === substatesEnum.pending
      ? substatesEnum.to_compensate
      : sidebarInfo.cashbackStatus.option === substatesEnum.compensating
      ? substatesEnum.conpensated
      : sidebarInfo.cashbackStatus.option;

  const gridTemplateColumns = "1fr 1.5fr 1.5fr 1.5fr 1fr 0.5fr";

  const [showConfimationModal, updateShowConfimationModal] = useState(false);

  const toogleConfirmationModal = () => {
    updateShowConfimationModal(!showConfimationModal);
  };

  const [showSuccessModal, updateShowSuccessModal] = useState(false);

  const toogleSuccessModal = () => {
    updateShowSuccessModal(!showSuccessModal);
  };

  const [showModalCompensation, updateShowModalCompensation] = useState(false);

  const toogleModalCompensation = () => {
    updateShowModalCompensation(!showModalCompensation);
  };

  const [modalSuccesCode, updateModalSuccesCode] = useState(false);
  const toogleModalSuccessCode = () => {
    updateModalSuccesCode(!modalSuccesCode);
  };

  const [isTypeEdit, updateIsTypeEdit] = useState(false);
  const [total, updateTotal] = useState(0);

  const formConfig = useFormConfig({
    createForm: (data) => {
      const operationsFg = data.operations.map(() => {
        const lineFg = new FormGroup({
          id: new FormControl(),

          info: new FormControl(),
          voucher_doc: new FormControl(),
          line: new FormGroup(
            {
              bank: new FormControl(),
              numeroOperacion: new FormControl(null, [
                Validators.required,
                Validators.pattern(/^[a-zA-Z0-9]{1,25}$/),
              ]),
            },
            null,
            operationNumberValidation(forceUpdate)
          ),
          montoDeposito: new FormControl(
            null,
            [Validators.required],
            montoDepositoValidator
          ),
          payment_date: new FormControl(null, [Validators.required]),
          operation_number_voucher: new FormControl(null, [
            Validators.required,
          ]),
        });
        return lineFg;
      });
      const form = new FormGroup({
        operations: new FormArray(operationsFg),
      });
      form
        .get("operations")
        .valueChanges.pipe(
          map((operations) => {
            return operations.reduce((t, e) => {
              let elm = 0;
              if (e.montoDeposito === ".") {
                elm = 0;
              } else {
                elm = +e.montoDeposito;
              }
              return t + elm;
            }, 0);
          }),
          tap((resp) => {
            updateTotal(resp);
          })
        )
        .subscribe();
      form.patchValue(data);
      return form;
    },
  });

  const { loadSucceed } = formConfig;

  useEffect(() => {
    const vouchers = sidebarInfo.vouchers;
    loadSucceed({
      operations: vouchers.map((voucher) => {
        return {
          id: voucher.voucher_id,
          info: voucher.bank_name.name,
          line: {
            bank: voucher.bank_name.option,
            numeroOperacion: "",
          },
          voucher_doc: voucher.voucher_doc,

          montoDeposito: voucher.amount ? voucher.amount : null,
          payment_date: new Date(sidebarInfo.dateInvoice),
        };
      }),
    });
    if (status === "to_compensate") {
      formConfig.form.disable();
    }
  }, [loadSucceed, formConfig, sidebarInfo, status]);

  const [displayPosition, setDisplayPosition] = useState(false);
  const statusBalance = (totalBilling, totalDeposited) => {
    const totalCalc = totalDeposited - totalBilling;
    const total = +totalCalc.toFixed(2);

    const statePayment =
      totalBilling > +totalDeposited.toFixed(2)
        ? statusAmount.faltante
        : totalBilling < totalDeposited && total > 0
        ? statusAmount.saldo_a_favor
        : statusAmount.exacto;

    return statePayment;
  };

  const amountFormat = (val, digits = 2) => {
    return Number(val).toLocaleString("en-US", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  };

  const dataAproved = useRef(null);

  const [haveErros, setHaveErros] = useState(false);

  const differenceAmount = (totalFact, totalDeposited) => {
    return totalFact > totalDeposited
      ? amountFormat(totalFact - totalDeposited)
      : totalFact < totalDeposited
      ? amountFormat(totalDeposited - totalFact)
      : amountFormat(totalFact - totalDeposited);
  };
  const changeFormatDate = (date) => {
    const parsedDate = parse(date, "yyyy-MM-dd", new Date());
    const formateDate = format(parsedDate, "dd/MM/yy");
    return formateDate;
  };
  return (
    <React.Fragment>
      <Toast ref={toast} />
      {showConfimationModal && (
        <ConfirmationModal
          haveBalance={
            statusBalance(
              formatMoneyNumber(
                sidebarInfo.salesInvoiceTotal,
                sidebarInfo.paymentCurrency === "usd"
              ),
              total
            ) === "Saldo a Favor"
              ? true
              : false
          }
          toggleModal={toogleConfirmationModal}
          sidebarInfo={sidebarInfo}
          dataFomr={formConfig.form.value}
          onSuccessData={(params) => {
            dataAproved.current = params;
            updateShowConfimationModal(false);
            updateShowSuccessModal(true);
          }}
          onErrorData={(err) => {
            updateShowConfimationModal(false);
            onErrorEndpoint(err);
          }}
        ></ConfirmationModal>
      )}

      {showSuccessModal && (
        <ModalOperationSuccess
          paymentCurrency={sidebarInfo.paymentCurrency}
          params={dataAproved.current}
          haveBalance={
            statusBalance(
              formatMoneyNumber(
                sidebarInfo.salesInvoiceTotal,
                sidebarInfo.paymentCurrency === "usd"
              ),
              total
            ) === "Saldo a Favor"
              ? true
              : false
          }
          balanceNum={amountFormat(
            total -
              formatMoneyNumber(
                sidebarInfo.salesInvoiceTotal,
                sidebarInfo.paymentCurrency === "usd"
              )
          )}
          toggleModal={() => {
            toogleSuccessModal();
            props.onOk();
          }}
          onOk={() => {
            updateShowSuccessModal(false);
            props.onOk();
          }}
        ></ModalOperationSuccess>
      )}

      {showModalCompensation && (
        <CompensationCode
          toggleModal={toogleModalCompensation}
          sidebarInfo={sidebarInfo}
          onErrorData={(err) => {
            updateShowConfimationModal(false);
            onErrorEndpoint(err);
          }}
          onSuccessData={() => {
            updateShowModalCompensation(false);
            updateModalSuccesCode(true);
          }}
        ></CompensationCode>
      )}
      {modalSuccesCode && (
        <AdminAskForAction
          toggleModal={() => {
            toogleModalSuccessCode();
            props.onOk();
          }}
          onApprove={() => {
            props.onOk();
          }}
          hideSidebar={() => props.onOk()}
          titleMessage={"Compensación exitosa"}
          bodyMessage={"La compensación con SAP a sido registrada con éxito."}
          buttonMessage={"Entendido"}
        />
      )}

      {displayPosition && (
        <ConpensationDetail
          toggleModal={() => {
            setDisplayPosition(false);
          }}
          sidebarInfo={sidebarInfo}
        />
      )}
      <Sidebar onOutsideClick={onOutsideClick}>
        <Container width="93.3rem">
          <div>
            <Title>PAGO Nº {sidebarInfo.invoiceNum}</Title>
            <Subtitle textTransform="lowercase">
              {`${sidebarInfo.vouchers.length} ${
                sidebarInfo.vouchers.length > 1 ? "vouchers" : "voucher"
              }  ${
                status === "rejected"
                  ? sidebarInfo.vouchers.length > 1
                    ? "rechazados"
                    : "rechazado"
                  : status === "pending_validation"
                  ? "por validar"
                  : status === "paid" && substatus === "conpensated"
                  ? sidebarInfo.vouchers.length > 1
                    ? "compensados"
                    : "compensado"
                  : "por compensar"
              }`}
            </Subtitle>
            <HorizontalLine />
            <ContainerInfo>
              {sidebarInfo.warningComment && (
                <MessageInformative
                  backgroundColor={
                    status === "rejected"
                      ? "rgba(255, 111, 97, 0.1)"
                      : "#FFF8DA"
                  }
                  color={status === "rejected" ? "#FF6F61" : "#000000"}
                >
                  <div className="icon-message">
                    <img
                      alt="info icon"
                      src={status === "rejected" ? icInfoRed : icInfoBlack}
                    />
                  </div>
                  <div className="message-info">
                    <PWarningComment width="75rem">
                      {sidebarInfo.warningComment}
                    </PWarningComment>
                  </div>
                </MessageInformative>
              )}
              <div
                style={{
                  display: "flex",
                  marginRight: "2rem",
                  justifyContent: "space-between",
                }}
              >
                <div style={{}}>
                  <SectionTitle>AGENTE DE ADUANA</SectionTitle>
                  <Text width="20rem">{sidebarInfo.agentName}</Text>
                </div>
                <div style={{}}>
                  <SectionTitle>TOTAL FACTURACIÓN</SectionTitle>
                  <Text>
                    {formatMoney(
                      sidebarInfo.salesInvoiceTotal,
                      sidebarInfo.paymentCurrency === "usd"
                    )}
                  </Text>
                </div>
                <div style={{}}>
                  <SectionTitle>TOTAL DEPOSITADO</SectionTitle>
                  <Text>
                    {sidebarInfo.paymentCurrency === "usd" ? "$" : "S/"}{" "}
                    {amountFormat(total)}
                  </Text>
                </div>
                <div>
                  <SectionTitle>DIFERENCIA DE MONTO</SectionTitle>

                  <AmountDifference
                    backgroundColor={
                      statusBalance(
                        formatMoneyNumber(
                          sidebarInfo.salesInvoiceTotal,
                          sidebarInfo.paymentCurrency === "usd"
                        ),
                        total
                      ) === "faltante"
                        ? "#FF6F61"
                        : statusBalance(
                            formatMoneyNumber(
                              sidebarInfo.salesInvoiceTotal,
                              sidebarInfo.paymentCurrency === "usd"
                            ),
                            total
                          ) === "Saldo a Favor"
                        ? "rgb(136, 177, 59)"
                        : "#FFFFFF"
                    }
                    labelColor={
                      statusBalance(
                        formatMoneyNumber(
                          sidebarInfo.salesInvoiceTotal,
                          sidebarInfo.paymentCurrency === "usd"
                        ),
                        total
                      ) === "Exacto"
                        ? "#000000"
                        : "rgb(255, 255, 255)"
                    }
                  >
                    <label htmlFor="test">
                      {`${
                        sidebarInfo.paymentCurrency === "usd" ? "$" : "S/"
                      } ${differenceAmount(
                        formatMoneyNumber(
                          sidebarInfo.salesInvoiceTotal,
                          sidebarInfo.paymentCurrency === "usd"
                        ),
                        total
                      )} (${statusBalance(
                        formatMoneyNumber(
                          sidebarInfo.salesInvoiceTotal,
                          sidebarInfo.paymentCurrency === "usd"
                        ),
                        total
                      )})`}{" "}
                    </label>
                    <span>
                      <img
                        width={"100%"}
                        height={"100%"}
                        src={
                          statusBalance(
                            formatMoneyNumber(
                              sidebarInfo.salesInvoiceTotal,
                              sidebarInfo.paymentCurrency === "usd"
                            ),
                            total
                          ) === "faltante"
                            ? icArrowCircleDownFill
                            : statusBalance(
                                formatMoneyNumber(
                                  sidebarInfo.salesInvoiceTotal,
                                  sidebarInfo.paymentCurrency === "usd"
                                ),
                                total
                              ) === "Saldo a Favor"
                            ? icArrowCircleUpFill
                            : icCircleCheckFill
                        }
                        alt="arrow up"
                      />
                    </span>
                  </AmountDifference>
                </div>
                <HorizontalContainer></HorizontalContainer>
              </div>
              {status === "paid" && (
                <HorizontalContainer>
                  <div style={{ width: "100%" }}>
                    <SectionTitle>NRO DE SERIE / FACTURA PDF</SectionTitle>
                    {pollPayment.quotations &&
                      pollPayment.quotations.map((quotation, i) => {
                        return (
                          <DivQuotationSide key={i}>
                            <DivFlexCenterQuotation>
                              <div style={{ marginRight: "1.3rem" }}>
                                <img
                                  alt="pdf"
                                  width={"100%"}
                                  height={"100%"}
                                  src={icPDF}
                                />
                              </div>
                              <LabelQuotationTci htmlFor="test">
                                {quotation.tci_num}
                              </LabelQuotationTci>
                            </DivFlexCenterQuotation>
                            {quotation.quotation_doc.length > 0 && (
                              <DivFlexCenterIcon
                                onClick={() => {
                                  handleDownload(quotation.quotation_doc);
                                }}
                              >
                                <img
                                  alt="down"
                                  src={icDownload}
                                  width={"100%"}
                                  height={"100%"}
                                />
                              </DivFlexCenterIcon>
                            )}
                          </DivQuotationSide>
                        );
                      })}
                  </div>
                </HorizontalContainer>
              )}

              <TableHeaderPers gridTemplateColumns={gridTemplateColumns}>
                <div className="left-align">BANCO</div>
                <div className="left-align" style={{ width: "98%" }}>
                  NÚMERO DE OPERACIÓN
                </div>
                <div className="left-align" style={{ width: "98%" }}>
                  NÚMERO OP. VOUCHER
                </div>
                <div className="left-align">MONTO DEPOSITADO</div>
                <div className="left-align" style={{ width: "98%" }}>
                  FECHA DE PAGO
                </div>
                <div></div>
              </TableHeaderPers>

              {status === "paid" && (
                <TableBodyPers gridTemplateColumns={gridTemplateColumns}>
                  {sidebarInfo.vouchers.map((voucher, i) => {
                    return (
                      <RowPers className="clickable-element" key={i}>
                        <div className="left-align">
                          {voucher.bank_name.name}
                        </div>
                        <div className="left-align">
                          {voucher.operation_number}
                        </div>
                        <div className="left-align">
                          {voucher.operation_number_voucher ? (
                            <>{voucher.operation_number_voucher}</>
                          ) : (
                            <DivJustifyScreenplay>-</DivJustifyScreenplay>
                          )}
                        </div>
                        <div className="left-align">
                          {sidebarInfo.paymentCurrency === "usd" ? "$" : "S/"}{" "}
                          {amountFormat(voucher.amount)}
                        </div>
                        <div className="left-align">
                          {voucher.payment_date ? (
                            <>{changeFormatDate(voucher.payment_date)}</>
                          ) : (
                            <DivJustifyScreenplay>-</DivJustifyScreenplay>
                          )}
                        </div>
                        <div>
                          <img
                            alt="download icon"
                            src={icDownload}
                            onClick={() => {
                              handleDownload(voucher.voucher_doc);
                            }}
                          />
                        </div>
                      </RowPers>
                    );
                  })}
                </TableBodyPers>
              )}

              {status === "rejected" && (
                <TableBodyPers gridTemplateColumns={gridTemplateColumns}>
                  {pollPayment.vouchers &&
                    pollPayment.vouchers.map((voucher, i) => {
                      return (
                        <RowPers className="clickable-element" key={i}>
                          <div className="left-align">
                            {voucher.bank_name.name}
                          </div>
                          <div
                            className="left-align"
                            style={{ color: "#C4C4C4" }}
                          >
                            {voucher.operation_number || "Sin Nº Op."}
                          </div>
                          <div
                            className="left-align"
                            style={{ color: "#C4C4C4" }}
                          >
                            {"Sin Nº Op."}
                          </div>
                          <div
                            className="left-align"
                            style={{ color: "#C4C4C4" }}
                          >
                            {"Sin Monto"}
                          </div>
                          <div
                            className="left-align"
                            style={{ color: "#C4C4C4" }}
                          >
                            {"Sin Fecha"}
                          </div>
                          <div>
                            <img
                              alt="download icon"
                              src={icDownload}
                              onClick={() => {
                                handleDownload(voucher.voucher_doc);
                              }}
                            />
                          </div>
                        </RowPers>
                      );
                    })}
                </TableBodyPers>
              )}

              {status === "pending_validation" && substatus === "not_required" && (
                <WForm formConfig={formConfig} style={{ width: "100%" }}>
                  <TableBodyPers gridTemplateColumns={gridTemplateColumns}>
                    <WFormArrayElement name="operations">
                      <RowPers className="clickable-element">
                        <WFormControl name="info">
                          {({ control }) => {
                            return (
                              <div className="left-align">{control.value}</div>
                            );
                          }}
                        </WFormControl>
                        <div className="left-align">
                          <WFormControl
                            name="line.numeroOperacion"
                            errorMessages={{
                              required: "Campo requerido",
                              pattern: "Campo inválido",
                            }}
                          >
                            <TemplateInputContainer
                              marginBottom="0"
                              position="absolute"
                            >
                              <WInputText
                                haveErros={haveErros}
                                width="90%"
                                height="3.2rem"
                                colorBorder="#C6C8CE"
                                placeholder="Ingresar Nº Op."
                                onKeyPress={onlyDigitsNumbersLetters}
                                maxLength={25}
                              />
                            </TemplateInputContainer>
                          </WFormControl>
                        </div>

                        <div className="left-align">
                          <WFormControl
                            name="operation_number_voucher"
                            errorMessages={{
                              required: "Campo requerido",
                              pattern: "Campo inválido",
                            }}
                          >
                            <TemplateInputContainer
                              marginBottom="0"
                              position="absolute"
                            >
                              <WInputText
                                haveErros={haveErros}
                                width="90%"
                                height="3.2rem"
                                colorBorder="#C6C8CE"
                                placeholder="Ingresar Nº voucher"
                                onKeyPress={onlyDigitsNumbersLetters}
                                maxLength={25}
                              />
                            </TemplateInputContainer>
                          </WFormControl>
                        </div>

                        <div className="left-align">
                          <WFormControl
                            name="montoDeposito"
                            errorMessages={{
                              exedQuantity: "Maximo 100,000,000.00",
                              required: "Campo requerido",
                              pattern: "Campo inválido",
                            }}
                          >
                            <TemplateInputContainer
                              marginBottom="0"
                              position="absolute"
                            >
                              <WInputCurrency
                                placeholder="Ingresar Monto"
                                colorBorder="#C6C8CE"
                                maxLength={12}
                                style={{
                                  width: "100%",
                                  height: "3.2rem",
                                }}
                                locale={
                                  sidebarInfo.paymentCurrency === "usd"
                                    ? "en-US"
                                    : "es-PE"
                                }
                                currency={
                                  sidebarInfo.paymentCurrency === "usd"
                                    ? "USD"
                                    : "PEN"
                                }
                              />
                            </TemplateInputContainer>
                          </WFormControl>
                        </div>

                        <div className="left-align">
                          <WFormControl name="payment_date">
                            <TemplateInputContainer
                              color="#C4C4C4"
                              marginBottom="0"
                              position="absolute"
                            >
                              <WCalendar
                                width="100%"
                                dateFormat="dd/mm/yy"
                                maxDate={new Date(sidebarInfo.dateInvoice)}
                              />
                            </TemplateInputContainer>
                          </WFormControl>
                        </div>
                        <div>
                          <WFormGroup>
                            {({ control }) => {
                              return (
                                <div
                                  style={{
                                    backgroundImage: `url(${icDownload})`,
                                    backgroundRepeat: "no-repeat",
                                    backgroundSize: "100% 100%",
                                    width: "2.4rem",
                                    height: "2.4rem",
                                  }}
                                  onClick={() => {
                                    const ctl = control;
                                    handleDownload(
                                      ctl.get("voucher_doc").value
                                    );
                                  }}
                                ></div>
                              );
                            }}
                          </WFormGroup>
                        </div>
                      </RowPers>
                      <WFormGroup name="line">
                        <GroupError setHaveErros={setHaveErros}>
                          {(errors) => {
                            return (
                              <ErrorMessage>
                                <label htmlFor="test">
                                  Nº Operación {errors.numberOperation} ya
                                  existe para Nº factura {errors.numberInvoice}
                                </label>
                              </ErrorMessage>
                            );
                          }}
                        </GroupError>
                      </WFormGroup>
                    </WFormArrayElement>
                  </TableBodyPers>
                </WForm>
              )}
            </ContainerInfo>
            <HorizontalLine />
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "row-reverse",
              justifyContent: "flex-end",
            }}
          >
            {status !== "paid" && (
              <div>
                <ButtonGeneric
                  height="6.2rem"
                  marginBottom="2.5rem"
                  onClick={() => {
                    setShowSidebarModal(true);
                  }}
                  width={status === "rejected" ? "87.3rem" : "27rem"}
                >
                  <img src={icIconDetail} alt="Aspa" />
                  <div style={{ marginLeft: "1.25rem" }}>Ver detalle</div>
                </ButtonGeneric>
              </div>
            )}

            {status === "pending_validation" && substatus === "not_required" && (
              <Authorization
                actions={["invoice.update_invoice_upfront_manual"]}
              >
                <ButtonsContainer>
                  <ApproveButton
                    disabled={
                      statusBalance(
                        formatMoneyNumber(
                          sidebarInfo.salesInvoiceTotal,
                          sidebarInfo.paymentCurrency === "usd"
                        ),
                        total
                      ) === "faltante" || !formConfig?.form?.valid
                    }
                    onClick={() => {
                      if (
                        statusBalance(
                          formatMoneyNumber(
                            sidebarInfo.salesInvoiceTotal,
                            sidebarInfo.paymentCurrency === "usd"
                          ),
                          total
                        ) === "Exacto"
                      ) {
                        handleAskForAction(
                          sidebarInfo,
                          formConfig.form.value,
                          "paid"
                        );
                      } else {
                        updateShowConfimationModal(true);
                      }
                    }}
                    width="27rem"
                    marginRight="2rem"
                  >
                    <img src={checkIcon} alt="Check" />
                    Aprobar
                  </ApproveButton>
                  <RejectButton
                    onClick={() => {
                      handleRejectionComment(sidebarInfo, "rejected");
                    }}
                    width="27rem"
                    marginRight="2rem"
                  >
                    <img src={redCrossIcon} alt="Aspa" />
                    Rechazar
                  </RejectButton>
                </ButtonsContainer>
              </Authorization>
            )}
            {status === "paid" && substatus === "to_compensate" && (
              <ButtonsContainer>
                <ApproveButton
                  onClick={() => {
                    updateShowModalCompensation(true);
                  }}
                  width="42rem"
                  marginRight="2rem"
                >
                  Compensar
                </ApproveButton>
                <ButtonGeneric
                  height="6.2rem"
                  marginBottom="2.5rem"
                  onClick={() => {
                    setShowSidebarModal(true);
                  }}
                  width="42rem"
                >
                  <img src={icIconDetail} alt="Aspa" />
                  <div style={{ marginLeft: "1.25rem" }}>Ver detalle</div>
                </ButtonGeneric>
              </ButtonsContainer>
            )}

            {isTypeEdit && (
              <ApproveButton
                width="100%"
                onClick={() => {
                  formConfig.form.disable();
                  updateIsTypeEdit(false);
                }}
              >
                <img src={icSaveFloppyDisk} alt="Check" />
                Guardar
              </ApproveButton>
            )}
            {status === "paid" && substatus === "conpensated" && (
              <ButtonsContainer>
                <DetailButton
                  width="42rem"
                  onClick={() => {
                    setDisplayPosition(true);
                  }}
                  marginRight="2rem"
                >
                  <img src={icChecklistRounded} alt="Check" />
                  Detalle de compensación
                </DetailButton>
                <ButtonGeneric
                  height="6.2rem"
                  marginBottom="2.5rem"
                  onClick={() => {
                    setShowSidebarModal(true);
                  }}
                  width="42rem"
                >
                  <img src={icIconDetail} alt="Aspa" />
                  <div style={{ marginLeft: "1.25rem" }}>Ver detalle</div>
                </ButtonGeneric>
              </ButtonsContainer>
            )}
          </div>
        </Container>
      </Sidebar>
    </React.Fragment>
  );
};

function generateDownloadUrl(id: string) {
  return networkBackendRequest(
    "/files/presigned-download-url/",
    HTTP_METHODS.GET,
    {
      object_name: id,
    }
  )
    .then((response) => response.json())
    .then((response) => response.url);
}
